/**
 * Skill detail — the shared Inspector grammar (design-system §5) over a
 * `/api/skills` facade record (one row in the unified `packages` store, scoped
 * to `package_type='skill'`; the skill `id` is the package id).
 *
 * Header carries the lifecycle workflow as the single primary action
 * (draft → review → published → archived via PATCH `/api/skills/:id`) plus the
 * named **compile** and **run / dispatch** package actions; Overview is a
 * FieldGrid; Body renders the markdown; Relations shows the skill's outgoing /
 * incoming `package_links`; Audit hits the generic timeline (teaser until it
 * ships). Input/output contracts render in the Body section as JSON/text.
 */
import * as React from "react";
import {
  useShow,
  useList,
  useGo,
  useDelete,
  type HttpError,
} from "@refinedev/core";
import {
  Archive,
  CheckCircle2,
  Pencil,
  Play,
  RotateCcw,
  Send,
  Trash2,
} from "@/components/ui/icon";

import type { Skill, PackageLink } from "@aos/shared";
import { Button } from "@/components/ui/button";
import { Markdown } from "@/components/app/MarkdownView";
import { ConfirmDialog } from "@/components/app/ConfirmDialog";
import {
  Inspector,
  InspectorHeader,
  InspectorSection,
  Field,
  FieldGrid,
  RefLink,
  RelationsBlock,
  AuditBlock,
  JsonOrText,
  ShowPageSkeleton,
  ShowPageError,
  TechnicalId,
  type InspectorAction,
} from "@/components/inspector";
import { formatDateTime } from "@/lib/format";
import { humanize } from "@/resources/projects/_form";
import { useApiAction } from "@/resources/_shared/useApiAction";
import { buildPackageRelations } from "@/resources/packages/_actions";
import { StatusAction } from "./_components/StatusAction";

export function SkillShow() {
  const go = useGo();
  const { mutate: deleteSkill } = useDelete();
  const [pendingDelete, setPendingDelete] = React.useState(false);
  const { query } = useShow<Skill, HttpError>({ resource: "skills" });
  const skill = query.data?.data;
  const refetch = () => {
    void query.refetch();
  };

  const { data: outgoing } = useList<PackageLink, HttpError>({
    resource: "package-links",
    filters: skill
      ? [{ field: "source_package_id", operator: "eq", value: skill.id }]
      : [],
    pagination: { pageSize: 200, mode: "server" },
    queryOptions: { enabled: Boolean(skill) },
  });
  const { data: incoming } = useList<PackageLink, HttpError>({
    resource: "package-links",
    filters: skill
      ? [{ field: "target_package_id", operator: "eq", value: skill.id }]
      : [],
    pagination: { pageSize: 200, mode: "server" },
    queryOptions: { enabled: Boolean(skill) },
  });

  if (query.isLoading) return <ShowPageSkeleton />;
  if (query.isError || !skill) {
    return (
      <ShowPageError
        message={query.error?.message ?? "Skill not found."}
        onRetry={refetch}
      />
    );
  }

  // The skill row IS a package row, so reuse the shared package-link relation
  // builder (it only reads `link_type` + the endpoint ids).
  const relations = buildPackageRelations(
    { id: skill.id } as never,
    outgoing?.data ?? [],
    incoming?.data ?? [],
  );

  const menuActions: InspectorAction[] = [
    {
      label: "Edit",
      icon: Pencil,
      onClick: () => go({ to: `/skills/${skill.id}/edit` }),
    },
    {
      label: "Delete",
      icon: Trash2,
      destructive: true,
      onClick: () => setPendingDelete(true),
    },
  ];

  return (
    <>
      <Inspector>
        <InspectorHeader
          title={skill.name}
          subtitle="A skill package — its contracts, body, lifecycle, and typed links."
          status={skill.status}
          actions={
            <>
              <CompileSkill skillId={skill.id} />
              <RunSkill skillId={skill.id} />
            </>
          }
          primaryAction={<ApprovalAction skill={skill} onDone={refetch} />}
          menuActions={menuActions}
        />

        <InspectorSection title="Overview">
          <FieldGrid>
            <Field label="Version">{skill.version}</Field>
            <Field label="Domain">{skill.domain}</Field>
            <Field label="Department">{skill.department}</Field>
            <Field label="Allowed executor">
              {skill.allowed_executor_type
                ? humanize(skill.allowed_executor_type)
                : null}
            </Field>
            <Field label="Owner">
              {skill.owner_user_id ? (
                <RefLink resource="users" id={skill.owner_user_id} />
              ) : null}
            </Field>
            <Field label="Created">{formatDateTime(skill.created_at)}</Field>
            <Field label="Updated">{formatDateTime(skill.updated_at)}</Field>
            {skill.short_summary ? (
              <Field label="Summary" full>
                {skill.short_summary}
              </Field>
            ) : null}
          </FieldGrid>
        </InspectorSection>

        {skill.body_markdown ? (
          <InspectorSection title="Body">
            <Markdown source={skill.body_markdown} />
          </InspectorSection>
        ) : null}

        <InspectorSection title="Contracts">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="space-y-2">
              <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Input contract
              </h4>
              <JsonOrText value={skill.input_contract} />
            </div>
            <div className="space-y-2">
              <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Output contract
              </h4>
              <JsonOrText value={skill.output_contract} />
            </div>
          </div>
        </InspectorSection>

        <InspectorSection title="Relations">
          <RelationsBlock groups={relations} />
        </InspectorSection>

        <InspectorSection title="Technical">
          <FieldGrid className="text-muted-foreground">
            <Field label="Id">
              <TechnicalId id={skill.id} />
            </Field>
          </FieldGrid>
        </InspectorSection>

        <InspectorSection title="Audit">
          <AuditBlock entityType="skills" entityId={skill.id} />
        </InspectorSection>
      </Inspector>

      <ConfirmDialog
        open={pendingDelete}
        onOpenChange={setPendingDelete}
        title="Delete skill?"
        description={
          <>
            This removes <span className="font-medium">{skill.name}</span>. This
            action cannot be undone.
          </>
        }
        confirmLabel="Delete"
        onConfirm={() =>
          new Promise<void>((resolve) => {
            deleteSkill(
              { resource: "skills", id: skill.id },
              {
                onSuccess: () => {
                  go({ to: "/skills" });
                  resolve();
                },
                onError: () => resolve(),
              },
            );
          })
        }
      />
    </>
  );
}

/** The lifecycle primary action appropriate to the skill's current status. */
function ApprovalAction({
  skill,
  onDone,
}: {
  skill: Skill;
  onDone: () => void;
}) {
  switch (skill.status) {
    case "draft":
      return (
        <StatusAction
          resource="skills"
          id={skill.id}
          toStatus="review"
          label="Submit for Review"
          icon={Send}
          onDone={onDone}
        />
      );
    case "review":
      return (
        <div className="flex items-center gap-2">
          <StatusAction
            resource="skills"
            id={skill.id}
            toStatus="draft"
            label="Back to Draft"
            icon={RotateCcw}
            variant="outline"
            onDone={onDone}
          />
          <StatusAction
            resource="skills"
            id={skill.id}
            toStatus="published"
            label="Publish"
            icon={CheckCircle2}
            onDone={onDone}
          />
        </div>
      );
    case "published":
      return (
        <StatusAction
          resource="skills"
          id={skill.id}
          toStatus="archived"
          label="Archive"
          icon={Archive}
          variant="outline"
          onDone={onDone}
        />
      );
    default:
      return null;
  }
}

/** `compile` action (`[PROPOSED]` `POST /api/packages/:id/compile`). */
function CompileSkill({ skillId }: { skillId: string }) {
  const { run, loading } = useApiAction();
  return (
    <Button
      variant="outline"
      size="sm"
      disabled={loading}
      data-testid="skill-compile"
      onClick={() =>
        void run(`/packages/${encodeURIComponent(skillId)}/compile`, {
          method: "POST",
          invalidateResources: ["skills", "packages"],
          successMessage: "Compilation requested.",
        }).catch(() => {
          /* [PROPOSED]: error surfaced as a toast, page stays alive. */
        })
      }
    >
      <CheckCircle2 className="mr-2 h-4 w-4" />
      {loading ? "Compiling…" : "Compile"}
    </Button>
  );
}

/** `run / dispatch` action (`[PROPOSED]` reuses the AgentRun dispatch path). */
function RunSkill({ skillId }: { skillId: string }) {
  const { run, loading } = useApiAction();
  return (
    <Button
      variant="outline"
      size="sm"
      disabled={loading}
      data-testid="skill-run"
      onClick={() =>
        void run(`/skills/${encodeURIComponent(skillId)}/dispatch`, {
          method: "POST",
          invalidateResources: ["agent-runs"],
          successMessage: "Skill dispatched.",
        }).catch(() => {
          /* [PROPOSED]: dispatch endpoint not yet wired — degrade to a toast. */
        })
      }
    >
      <Play className="mr-2 h-4 w-4" />
      {loading ? "Dispatching…" : "Run"}
    </Button>
  );
}

export default SkillShow;
